import { Component, Input, OnChanges } from '@angular/core';
import { Product, ProductLine } from '../../../model/product/product.model';
import { lineColorSet } from '../line-color-set';
import { mixHex } from '../line-electric-colors';

/**
 * A single LINE-theme preview for one theme, the theme rendered as a real
 * LINE chat screen (HTML/CSS, not a flattened PNG) inside an iPhone frame.
 *
 * The chat background is the theme's wallpaper, the top/bottom bars use the
 * theme's `surface` token, and the outgoing bubble uses the theme's accent
 * (`designTokens.primaryColor`). It mirrors LINE's real chat UI: an iOS status
 * bar (time + signal/wifi/battery), a header (back / avatar / name /
 * "Automated responses" / search + menu), left/right bubbles, and the bottom
 * input bar.
 *
 * Driven by its `product` input. The team screenshots one card (via the
 * `/line?only=<key>` capture mode) to drop a clean preview onto the LINE store.
 */
@Component( {
  selector: 'app-line-card',
  templateUrl: './line-card.component.html',
  styleUrls: [ './line-card.component.scss' ],
} )
export class LineCardComponent implements OnChanges {

  /** The theme this preview renders. */
  @Input() product!: Product;

  /** Owning product line (kept for API parity; unused in this layout). */
  @Input() productLine?: ProductLine;

  /** iPhone outline frame (transparent interior) drawn over the chat screen. */
  readonly frameSrc = './assets/common/iphone-17-frame.webp';

  // Derived view state, recomputed whenever the inputs change.
  name = '';
  logo = '';
  background = '';
  surface = '#1A3746';
  accent = '#549EFF';
  foreground = '#ECF0F4';
  /** Text colour that reads on the accent bubble (dark on light accents). */
  accentText = '#0E1216';
  /** Incoming-bubble background, derived from surface. */
  incoming = '#3A3F46';
  /** Ink on the incoming bubble. Usually the base set's `fg`, but a skin whose
   *  `fg` is white and whose incoming bubble is near-white needs its own. */
  incomingText = '#ECF0F4';
  /** Hairline around the incoming bubble. Only needed when the bubble and the
   *  wallpaper behind it are the same colour (Everest's white-on-white skin);
   *  transparent everywhere else, exactly as LINE draws it. */
  incomingBorder = 'transparent';
  /** The same hairline on the OUTGOING bubble. Added for Salda, whose sampled
   *  skin makes BOTH bubbles white and separates them by outline colour alone —
   *  pale green on the incoming, pale pink on the outgoing. Everest only ever
   *  needed the incoming hook because its outgoing bubble is a filled blue. */
  outgoingBorder = 'transparent';
  /**
   * Wallpaper scrim baked over the chat asset: dark themes dim the artwork with
   * a black wash; LIGHT themes frost it with a WHITE glass wash instead (owner
   * 2026-07-17: a light theme's chat background must read as bright frosted
   * glass, not the dark haze). Mirrors the baked asset in line.js.
   */
  scrim = 'rgba(0, 0, 0, 0.25)';

  /**
   * Themes whose chat wallpaper is the new TRANSPARENT kawaii illustration
   * (app-line-chat-bg) rendered over a flat color-skin sky, rather than the old
   * photo + scrim. When true, the preview drops the photo/scrim and shows the
   * live component over `skinColor` — exactly what LINE renders.
   */
  useKawaii = false;
  /** The flat color-skin sky the kawaii wallpaper floats over. */
  skinColor = '#FCEAF2';

  // Top (header/status) and bottom (input) bar colours. Default to the base set;
  // Sakura mirrors LINE's real chat: a PINK header with white icons and a
  // PALE-PINK input bar with pink icons + a light-pink text pill. All hexes
  // pixel-sampled from the owner's chat screenshot.
  headerBg = '#1A3746';
  headerText = '#ECF0F4';
  inputBg = '#1A3746';
  inputFg = '#ECF0F4';
  inputPill = 'rgba(255,255,255,0.14)';
  /** Emoji glyph INSIDE the (light) text pill. Empty → falls back to inputFg;
   *  set it when the pill colour differs enough that inputFg would vanish on it
   *  (Tokyo: white pill needs a purple emoji, not the white input icons). */
  emojiFill = '';

  ngOnChanges(): void {
    const product = this.product;
    if ( !product ) {
      return;
    }

    this.name = product.name || '';
    this.logo = product.logo || '';
    this.background = product.backgroundImages?.length
      ? product.backgroundImages[ 0 ].src
      : '';
    // Real dark LINE chat: dark bars + a dark-grey incoming bubble. The OUTGOING
    // bubble takes the THEME's own colour (from designTokens), not LINE green,     // that's what a real applied theme does. The wallpaper is the theme too.
    // Bubble + surface colours come from the chosen predefined base set (NOT
    // designTokens); only the wallpaper above is the theme's own asset.
    const set = lineColorSet( product.key, product.appearance );
    // The top/bottom bars use the home screen's background so the two previews'
    // colours stay consistent.
    this.surface = set.bg;
    this.foreground = set.fg;
    this.incoming = set.incoming;
    this.incomingText = set.incomingText || set.fg;
    this.accent = set.outgoing;
    this.accentText = set.outgoingText;
    const light = ( product.appearance || '' ).toLowerCase() === 'light';
    this.scrim = light ? 'rgba(255, 255, 255, 0.34)' : 'rgba(0, 0, 0, 0.25)';

    // Bars default to the base set (bars = bg, icons = fg).
    this.headerBg = this.surface;
    this.headerText = this.foreground;
    this.inputBg = this.surface;
    this.inputFg = this.foreground;
    this.inputPill = 'rgba(255,255,255,0.14)';
    this.incomingBorder = 'transparent';
    this.outgoingBorder = 'transparent';
    // Sakura: pink header + white icons, pale-pink input bar + pink icons + a
    // light-pink text pill (pixel-sampled from the chat screenshot).
    if ( product.key === 'sakura' ) {
      this.headerBg = '#FEA5B9';
      this.headerText = '#FFFFFF';
      this.inputBg = '#FFE0E7';
      this.inputFg = '#FEA5B9';
      this.inputPill = '#FECBD7';
    }
    // Everest: the owner's stock LINE skin — a WHITE header with blue icons over
    // a white input bar with blue icons and a grey text pill. Pixel-sampled from
    // the owner's chat screenshot.
    if ( product.key === 'everest' ) {
      this.headerBg = '#FFFFFF';
      this.headerText = '#3D87C2';
      this.inputBg = '#FFFFFF';
      this.inputFg = '#3D87C2';
      this.inputPill = '#E8E8E8';
      // White bubbles on a white wallpaper: LINE separates them with a hairline.
      this.incomingBorder = '#EDF3F8';
    }
    // Aurora: LINE's near-black skin — the bars are the same near-black as the
    // body, with a mid-grey text pill. Pixel-sampled from the owner's chat
    // screenshot; the bubbles come from the AURORA set above.
    if ( product.key === 'aurora-borealis' ) {
      this.headerBg = '#131313';
      this.headerText = '#FFFFFF';
      this.inputBg = '#131313';
      this.inputFg = '#FFFFFF';
      this.inputPill = '#2C2C2C';
    }
    // Sequoia: the owner's GREEN LINE skin — a green header with white icons
    // over a DEEP green input bar whose icons are mint. Pixel-sampled from the
    // chat screenshot.
    if ( product.key === 'sequoia' ) {
      this.headerBg = '#22A255';
      this.headerText = '#FFFFFF';
      this.inputBg = '#198444';
      this.inputFg = '#6CD99B';
      this.inputPill = '#EBEBDE';
    }
    // Autumn: the owner's chosen skin — an APRICOT header with brown icons over
    // a CARAMEL input bar whose icons are white and whose text pill is white.
    // The two bars being different colours is this skin's own idea, not ours;
    // all four hexes are pixel-sampled from the chat screenshot.
    if ( product.key === 'autumn' ) {
      this.headerBg = '#FEE6C1';
      this.headerText = '#7D4C34';
      this.inputBg = '#C7813B';
      this.inputFg = '#FFFFFF';
      this.inputPill = '#FFFFFF';
      this.emojiFill = '#C7813B';   // caramel emoji on the white pill
    }
    // Roraima: the owner's dark-brown skin, every hex pixel-sampled from their
    // chat screenshot (2026-08-13). The header is the same brown as the body, so
    // the chat screen has no band at the top; the input bar is a step DARKER
    // than the body with a darker still text pill, which is the opposite of the
    // light skins in this family and is also what lineTabFill() reads.
    if ( product.key === 'roraima' ) {
      this.headerBg = '#5C3E30';
      this.headerText = '#FFFFFF';
      this.inputBg = '#422C21';
      this.inputFg = '#926A51';   // muted copper icons
      this.inputPill = '#3C251C'; // the text pill, darker than the bar
    }
    // Miami: the owner's hot-pink skin — a pink header with white icons over a
    // pale pink input bar whose icons and text pill are pinker still. Sampled
    // from the chat screenshot.
    if ( product.key === 'miami' ) {
      this.headerBg = '#FA5392';
      this.headerText = '#FFFFFF';
      this.inputBg = '#FFD8E7';
      this.inputFg = '#FD5D9B';
      this.inputPill = '#FECDE0';
    }
    // Istanbul: the owner's bright-cyan skin, every hex pixel-sampled from the
    // four device screenshots they shot on 2026-08-14, so nothing here is derived
    // from the theme's designTokens. Header and input bar are the SAME bright
    // cyan as the skin's surface, which is why the chat reads as one cyan band
    // wrapped around a very pale body rather than as two different bars. The
    // icons are white because that is the only ink that survives on a cyan this
    // saturated, and the text pill is a step DEEPER than the bar so it still
    // separates from the bar it sits inside; the white icons read on that
    // deeper cyan on their own, so this skin needs no emojiFill override.
    if ( product.key === 'istanbul' ) {
      this.headerBg = '#4AC3E0';
      this.headerText = '#FFFFFF';
      this.inputBg = '#4AC3E0';
      this.inputFg = '#FFFFFF';
      this.inputPill = '#2EAACB'; // the text pill, a step deeper than the bar
    }
    // Paris: PLACEHOLDER — these five hexes are RORAIMA's, copied verbatim. The
    // owner has not picked Paris's LINE colour skin yet and will set it
    // themselves; until then the chat preview borrows Roraima's dark-brown bars
    // so it renders on something plausible. Nothing here is sampled from a Paris
    // screenshot or derived from the theme's designTokens.
    // TODO(owner): resample all five from the real skin's device screenshots.
    // Paris: the owner's rose skin, pixel-sampled from their device screenshots
    // (2026-08-14). The header is the warm rose and the input bar is a PALER pink
    // than it, which is unusual in this family and is the one value here that has
    // to be right for a reason beyond looks — the classic tab strip ends on the
    // input bar, so an eyeballed header colour there would show a seam under the
    // menu background on the phone.
    if ( product.key === 'paris' ) {
      this.headerBg = '#FE7898';
      this.headerText = '#FFFFFF';
      this.inputBg = '#FDBCC8';
      this.inputFg = '#FFFFFF';
      this.inputPill = '#FECED8';   // the text pill, a shade lighter than the bar
      this.emojiFill = '#FE7898';   // the pill is LIGHTER than the bar here, so the
                                    // white icons need a rose fill to read on it
    }
    // Tallinn on the owner's chosen skin (screenshots 2026-08-14 22.06): an azure
    // header with white icons over a near-WHITE input bar with dark grey ones —
    // the two bars are nothing like each other, which is unusual here and is what
    // makes the tab fill white rather than blue.
    if ( product.key === 'tallinn' ) {
      this.headerBg = '#0D8DC3';
      this.headerText = '#FFFFFF';
      this.inputBg = '#F9F9F9';
      this.inputFg = '#5D5D5D';   // dark grey icons on the near-white bar
      this.inputPill = '#EAEAEA'; // the text pill, a step down from the bar
    }
    // Rio on the owner's SECOND skin, pixel-sampled from their device screenshots
    // (2026-08-14 21.23) after the stock green went back for looking like
    // Sequoia's. The chat screen is where this skin puts its colour: a plain grey
    // header with dark icons, and a GREEN input bar under it whose text pill is a
    // step deeper still, with pale-green icons on it.
    //
    // The bar is the value that matters beyond looks — the classic tab strip has
    // to end on it, so this hex and lineTabFill('rio-de-janeiro') must stay equal.
    if ( product.key === 'rio-de-janeiro' ) {
      this.headerBg = '#F6F6F6';
      this.headerText = '#121212';   // dark icons on the grey band, not white
      this.inputBg = '#00A64B';      // the skin's green, the owner's own value
      this.inputFg = '#9FDBB9';      // pale green icons (sampled)
      this.inputPill = '#00913F';    // the text pill, a step deeper than the bar
    }
    // Tokyo: dark-plum header (white icons) + a medium-purple input bar with
    // white icons and a white text pill (pixel-sampled from the chat mockup).
    // Ocean: the SAMPLED light skin (see line-color-set's OCEAN). The bright teal
    // carries the header AND the input bar — one band top and bottom, the shape
    // Rio has — with a pale mint text pill inside the lower one. The talk room
    // between them is near-white.
    if ( product.key === 'ocean' ) {
      this.headerBg = '#2FB8A7';
      this.headerText = '#FFFFFF';
      this.inputBg = '#2FB8A7';
      // NOTE: the talk room is NOT set here. There is exactly one chain that
      // decides skinColor, further down, and it runs AFTER these branches — a
      // value written here is silently overwritten. That is what happened on the
      // first Ocean pass: the chat preview came out in the default pale pink
      // while every other Ocean surface was deep water.
      this.inputFg = '#FFFFFF';     // white icons on the teal bar (sampled)
      this.inputPill = '#95E9DD';   // the text pill, a pale mint INSIDE the bar (sampled)
      // The white input icons would vanish on that pale pill, so the emoji inside
      // it takes the bar's own teal instead — the Tokyo case exactly.
      this.emojiFill = '#2FB8A7';
    }
    // Salda: the SAMPLED sand-and-lake skin. The shape held — a lone turquoise
    // header, an input bar that is NOT the header's colour — but the bar is
    // SALMON, not the pale sand this was derived as, and that is the one value no
    // amount of reasoning from the palette would have found.
    if ( product.key === 'salda' ) {
      this.headerBg = '#70CACE';
      this.headerText = '#FFFFFF';
      this.inputBg = '#F5B6A6';     // salmon (sampled), not sand
      this.inputFg = '#FFFFFF';     // white icons on the salmon bar (sampled)
      this.inputPill = '#FFF9EC';   // the text pill, near-white (sampled)
      // Both bubbles are WHITE on this skin and the phone separates them with a
      // hairline: pale green on the incoming, pale pink on the outgoing. Without
      // these two the preview draws one white shape twice.
      this.incomingBorder = '#E5F3C9';
      this.outgoingBorder = '#FCE4E1';
      // White input icons would vanish on the near-white pill, so the emoji
      // inside it takes the bar's own salmon.
      this.emojiFill = '#F5B6A6';
    }
    // Prague: the SAMPLED near-black skin. The bars are the SAME near-black as the
    // body rather than a step up from it — there is no band here at all, which is
    // the shape Aurora's skin has and the opposite of what this was derived as.
    if ( product.key === 'prague' ) {
      this.headerBg = '#131313';
      this.headerText = '#FFFFFF';
      this.inputBg = '#131313';
      this.inputPill = '#2C2C2C';   // the text pill, a step up from the bar (sampled)
    }
    // Sydney: the derived harbour skin. Blue header, PALE HARBOUR input bar —
    // not the header's blue, which is what lets the menu strip's own water run
    // into the tab bar without an edge (see TAB_FILL).
    if ( product.key === 'sydney' ) {
      this.headerBg = '#0D8DC3';
      this.headerText = '#FFFFFF';
      this.inputBg = '#F9F9F9';     // neutral near-white bar (sampled)
      this.inputFg = '#0D8DC3';     // harbour-blue icons on it
      this.inputPill = '#EAEAEA';   // LINE's grey text pill (sampled)
    }

    // Reynisfjara: the derived Nordic skin. One grey on the header and the input
    // bar alike, a step up from the talk room — the shape every dark skin takes.
    if ( product.key === 'reynisfjara' ) {
      this.headerBg = '#131313';
      this.headerText = '#FFFFFF';
      this.inputBg = '#131313';
      this.inputFg = '#FFFFFF';
      this.inputPill = '#2C2C2C';   // the text pill, a step up from the bar (sampled)
    }

    // Spring: the derived meadow skin. Green header, PALE MEADOW input bar — not
    // the header's green, which is what lets the strip's own foreground grass run
    // into the tab bar without an edge (see TAB_FILL).
    if ( product.key === 'spring' ) {
      this.headerBg = '#FFFFFF';
      this.headerText = '#91C23D';   // green ink on white, not white on colour
      this.inputBg = '#FFFFFF';
      this.inputFg = '#91C23D';
      this.inputPill = '#E6F6CE';    // the meadow green, inside the white bar
    }

    // Vienna: the SAMPLED skin. LINE's vermilion on the header, a near-white bar
    // under the chat — the two are not the same surface here, which is what every
    // light skin in this family does and the opposite of the wine-on-wine the
    // derived version had.
    if ( product.key === 'vienna' ) {
      this.headerBg = '#EE4333';
      this.headerText = '#FFFFFF';
      this.inputBg = '#FAF4EF';
      this.inputFg = '#A85E2C';     // sienna icons on the pale bar, not white
      this.inputPill = '#FFFFFF';   // the text pill, a step LIGHTER than the bar
    }
    // Madrid: the SAMPLED skin. The header is the same saffron as the body, with
    // dark ink on it — this skin has no coloured header band at all, which is why
    // headerBg equals the talk room. The input bar is the one place it does step,
    // to a pale butter.
    if ( product.key === 'madrid' ) {
      this.headerBg = '#FFDD66';
      this.headerText = '#3A2F08';   // 9.93:1; white measures 1.33 here
      this.inputBg = '#FFF5CD';
      this.inputFg = '#8C7A3A';      // muted ochre icons on the butter bar
      this.inputPill = '#FFFFFF';    // the text pill, a step LIGHTER than the bar
    }
    // Etna: the SAMPLED basalt skin.
    // Etna: the SAMPLED skin. The header is the same near-black as the body — this
    // skin has no coloured or lifted header band at all — and the input bar sits on
    // it with only the text pill stepping up.
    if ( product.key === 'etna' ) {
      this.headerBg = '#131313';
      this.headerText = '#FFFFFF';
      this.inputBg = '#131313';
      this.inputPill = '#2C2C2C';   // the text pill, the one step this bar takes
    }
    // Fuji: the SAMPLED dawn skin. Coral header, and an input bar that is ITS OWN
    // deeper coral rather than the blush — the one place this skin does not simply
    // repeat the body.
    if ( product.key === 'fuji' ) {
      this.headerBg = '#FE939C';
      this.headerText = '#FFFFFF';
      this.inputBg = '#FEB8B7';
      this.inputFg = '#FFFFFF';     // white icons on the coral bar
      this.inputPill = '#FEC5C4';   // the text pill, a shade up from the bar
    }
    if ( product.key === 'tokyo' ) {
      this.headerBg = '#51385E';
      this.headerText = '#FFFFFF';
      this.inputBg = '#886399';
      this.inputFg = '#FFFFFF';
      this.inputPill = '#FFFFFF';
      this.emojiFill = '#886399';   // purple emoji on the white pill (icons stay white)
    }

    // Kawaii transparent-wallpaper themes (Sakura first): the sky is a flat
    // color skin, the component carries only the drifting petals.
    // Themes with a live, transparent chat-bg scene (drifting petals / twinkles)
    // over a flat colour skin, instead of a blurred wallpaper photo.
    this.useKawaii = product.key === 'sakura' || product.key === 'tokyo'
      || product.key === 'everest' || product.key === 'aurora-borealis'
      || product.key === 'sequoia' || product.key === 'autumn'
      || product.key === 'roraima' || product.key === 'miami'
      || product.key === 'ocean' || product.key === 'salda'
      || product.key === 'prague' || product.key === 'sydney'
      || product.key === 'reynisfjara' || product.key === 'spring'
      || product.key === 'vienna' || product.key === 'madrid'
      || product.key === 'etna' || product.key === 'fuji'
      || product.key === 'istanbul' || product.key === 'paris'
      || product.key === 'rio-de-janeiro' || product.key === 'tallinn';
    const t: any = product.designTokens || {};
    this.skinColor = product.key === 'tokyo'
      ? '#B99EC5'                                        // Tokyo chat skin (mauve)
      : product.key === 'everest'
        ? '#FFFFFF'                                      // the owner's LINE skin: white chat sky
        : product.key === 'aurora-borealis'
          ? '#131313'                                    // the owner's LINE skin: near-black night
          : product.key === 'sequoia'
            ? '#EBEBDE'                                  // the skin's warm pale chat sky
            : product.key === 'autumn'
              ? '#FFF1DD'                                // the skin's cream talk-room sky
              : product.key === 'roraima'
                ? '#5C3E30'                              // the skin's dark-brown talk room (sampled)
                : product.key === 'miami'
                  // The ONE value in this skin not readable from the screenshots:
                  // Miami's shipped wallpaper is still opaque, so it covers the
                  // talk room's own colour. This is the skin's palest sampled
                  // pink (its search field), and it wants confirming on the
                  // device once the transparent wallpaper is uploaded.
                  ? '#FFF2F6'
                  : product.key === 'istanbul'
                    // Istanbul's talk room is the very pale cyan that sits behind
                    // the bubbles in the owner's device screenshots, so that is
                    // the sky its transparent wallpaper composites over.
                    ? '#F2FDFF'
                    : product.key === 'paris'
                      // Paris's talk room, sampled from the device: a near-white
                      // pink, a shade warmer than its friends-list body. This is
                      // the sky its transparent wallpaper composites over.
                      ? '#FFF1F2'
                      : product.key === 'rio-de-janeiro'
                        // Rio's talk room, sampled: a light NEUTRAL grey. This is
                        // the sky its transparent wallpaper composites over, and
                        // it is not a green — every kite tint on that wallpaper is
                        // aimed at this grey.
                        ? '#ECECEC'
                        : product.key === 'tallinn'
                          // Tallinn's talk room, sampled: a pale winter blue. This
                          // is the sky its snowflakes composite over.
                          ? '#D3EAF2'
                          : product.key === 'ocean'
                            // Ocean's talk room, SAMPLED: the palest sea-mist,
                            // not the deep water this theme was built for. This
                            // is the sky its jellyfish composite over, and every
                            // tint on that wallpaper had to be inverted to dark
                            // marks because of it.
                            ? '#EDFAF9'
                            : product.key === 'salda'
                              // Salda's talk room, SAMPLED: a warm CREAM, not the
                              // palest turquoise this was derived as. It is the
                              // sky its shells composite over, so every tint on
                              // that wallpaper aims at this hex — which is why
                              // they had to be re-judged when it moved.
                              ? '#FFF8E8'
                              : product.key === 'prague'
                                // Prague's talk room, SAMPLED: LINE's near-black,
                                // not the navy night this was derived as. It is
                                // the sky its lamps composite over, so every tint
                                // on that wallpaper aims at this hex.
                                ? '#131313'
                                : product.key === 'sydney'
                                  // Sydney's talk room, derived: the palest
                                  // harbour. Every sail tint on that wallpaper is
                                  // aimed at this exact hex.
                                  ? '#D3EAF2'
                                  : product.key === 'reynisfjara'
                                    // Reynisfjara's talk room, derived: the
                                    // theme's own dark. Every basalt tint on that
                                    // wallpaper is aimed at this exact hex.
                                    ? '#131313'
                                    : product.key === 'spring'
                                      // Spring's talk room, derived: the palest
                                      // meadow. Every bird tint on that wallpaper
                                      // is aimed at this exact hex.
                                      ? '#FFFFFF'
                                      : product.key === 'vienna'
                                        // Vienna's talk room, SAMPLED: a warm
                                        // blush. Every violin tint on that
                                        // wallpaper is aimed at this hex.
                                        ? '#F8EDE7'
                                        : product.key === 'madrid'
                                          // Madrid's talk room, SAMPLED: the one
                                          // saffron this whole skin is built from.
                                          // Every churro tint on that wallpaper is
                                          // aimed at this hex.
                                          ? '#FFDD66'
                                          : product.key === 'etna'
                                            // Etna's talk room, SAMPLED: LINE's
                                            // own near-black. Every ember tint on
                                            // that wallpaper is aimed at it.
                                            ? '#131313'
                                            : product.key === 'fuji'
                                              // Fuji's talk room, SAMPLED: the
                                              // blush. Every mountain tint
                                              // on that wallpaper is aimed at it.
                                              ? '#FFE8E1'
          : mixHex( '#FFFFFF', t.primaryColor || '#E898B4', 0.10 );
  }

  /** A compactPalette colour by index, or undefined. */
  private pick( product: Product, i: number ): string | undefined {
    const palette = product.compactPalette?.length ? product.compactPalette : product.palette;
    return palette && palette[ i ];
  }

  /** Perceived luminance (0-255) of a hex colour. */
  private luminance( hex: string ): number {
    const [ r, g, b ] = this.rgb( hex );
    return 0.299 * r + 0.587 * g + 0.114 * b;
  }

  /** Blend `hex` toward `toward` by `f` (0..1). */
  private mix( hex: string, toward: string, f: number ): string {
    const a = this.rgb( hex );
    const b = this.rgb( toward );
    const c = a.map( ( v, i ) => Math.round( v + ( b[ i ] - v ) * f ) );
    return '#' + c.map( v => v.toString( 16 ).padStart( 2, '0' ) ).join( '' );
  }

  private rgb( hex: string ): number[] {
    const h = ( hex || '#000000' ).replace( '#', '' );
    const full = h.length === 3 ? h.split( '' ).map( x => x + x ).join( '' ) : h;
    return [ 0, 2, 4 ].map( i => parseInt( full.substr( i, 2 ), 16 ) || 0 );
  }
}
